import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@web/shared/ui/badge';
import {
  Kanban,
  KanbanBoard,
  KanbanCardTitle,
  KanbanColumn,
  KanbanColumnContent,
  KanbanColumnHeader,
  KanbanEmpty,
  KanbanItem,
  KanbanOverlay,
} from '@web/shared/ui/kanban';
import { Switch } from '@web/shared/ui/switch';
import { Label } from '@web/shared/ui/label';
import { Page } from '@web/shared/ui/page';

/**
 * `/theme/kanban` — o Kanban do design system COM arrastar e soltar ligado: cartões entre
 * colunas e dentro da mesma coluna, colunas reordenáveis pela barra. A chave desliga tudo,
 * para se ver o quadro só leitura.
 */
export const Route = createFileRoute('/theme/kanban')({
  component: KanbanDemoPage,
});

interface Card {
  id: string;
  title: string;
  who: string;
  tag?: string;
}

const INITIAL: Record<string, Card[]> = {
  todo: [
    { id: 'c1', title: 'Proposta — plano anual', who: 'Ana Souza', tag: 'Vendas' },
    { id: 'c2', title: 'Retorno sobre orçamento', who: 'Carlos Lima', tag: 'Vendas' },
    { id: 'c3', title: 'Agendar demonstração', who: 'Beatriz Alves' },
  ],
  doing: [
    { id: 'c4', title: 'Negociação de desconto', who: 'Diego Martins', tag: 'Financeiro' },
    { id: 'c5', title: 'Reunião de diagnóstico', who: 'Fernanda Luz' },
  ],
  review: [{ id: 'c6', title: 'Revisão do contrato', who: 'Ana Souza' }],
  done: [
    { id: 'c7', title: 'Onboarding concluído', who: 'Carlos Lima', tag: 'Sucesso' },
    { id: 'c8', title: 'Primeira cobrança emitida', who: 'Beatriz Alves' },
  ],
};

const TITLES: Record<string, string> = {
  todo: 'A fazer',
  doing: 'Em andamento',
  review: 'Em revisão',
  done: 'Concluído',
};

function KanbanDemoPage() {
  const [value, setValue] = useState(INITIAL);
  const [dragEnabled, setDragEnabled] = useState(true);
  const [last, setLast] = useState<string | null>(null);
  const find = (id: string) =>
    Object.values(value)
      .flat()
      .find((card) => card.id === id);

  return (
    <Page scroll="content" className="flex-col px-6 pt-4 pb-4">
      <div className="mb-4 flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="type-title3">Kanban</h1>
          <p className="type-caption1 text-fg-tertiary">
            {last ?? 'Arraste um cartão pela alça, ou uma coluna pela barra.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="tema-kanban-drag" checked={dragEnabled} onCheckedChange={setDragEnabled} />
          <Label htmlFor="tema-kanban-drag">Arrastar e soltar</Label>
        </div>
      </div>

      <Kanban
        value={value}
        onValueChange={setValue}
        getItemValue={(card) => card.id}
        dragEnabled={dragEnabled}
        onValueCommit={(_next, meta) =>
          setLast(
            meta.kind === 'column'
              ? `Coluna "${TITLES[meta.activeContainer]}" foi para a posição ${meta.overIndex + 1}.`
              : `"${find(String(meta.event.active.id))?.title}" foi de ${TITLES[meta.activeContainer]} para ${TITLES[meta.overContainer]}.`,
          )
        }
        restoreOnCancel
      >
        <KanbanBoard>
          {Object.keys(value).map((column) => (
            <KanbanColumn key={column} value={column}>
              <KanbanColumnHeader
                title={TITLES[column]}
                count={value[column]?.length ?? 0}
                tone="brand-ramp"
              />
              <KanbanColumnContent value={column}>
                {value[column]?.length === 0 && <KanbanEmpty>Nada por aqui.</KanbanEmpty>}
                {value[column]?.map((card) => (
                  <DemoCard key={card.id} card={card} />
                ))}
              </KanbanColumnContent>
            </KanbanColumn>
          ))}
        </KanbanBoard>
        <KanbanOverlay>
          {({ value: id, variant }) => {
            if (variant === 'column') {
              const key = String(id);
              return (
                <KanbanColumn value={key} className="w-[280px]">
                  <KanbanColumnHeader title={TITLES[key]} count={value[key]?.length ?? 0} />
                </KanbanColumn>
              );
            }
            const card = find(String(id));
            return card ? <DemoCard card={card} /> : null;
          }}
        </KanbanOverlay>
      </Kanban>
    </Page>
  );
}

function DemoCard({ card }: { card: Card }) {
  return (
    <KanbanItem value={card.id}>
      <KanbanCardTitle
        actions={
          card.tag && (
            <Badge variant="outline" className="type-caption1-strong">
              {card.tag}
            </Badge>
          )
        }
      >
        {card.title}
      </KanbanCardTitle>
      <span className="type-caption1 text-fg-tertiary truncate">{card.who}</span>
    </KanbanItem>
  );
}
